import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useServiceList } from "@/contexts/ServiceListContext";
import { Button } from "@/components/ui/button";
import TherapeuticTreatmentsModal from "./modals/TherapeuticTreatmentsModal";
import { ArrowRight, Plus, Minus, ChevronDown, Zap, Dumbbell, BarChart3, Star, Check, TrendingUp } from "lucide-react";
import { GlassContainer, GlassCard } from "@/components/experimental/GlassUI";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { OptimizedImage } from "@/components/OptimizedImage";
import { extractDurationPricePairs, getSingleLanguageTitle, extractDurations } from "@/utils/spaUtils";
import { getSpaBlurPlaceholder } from "@/utils/spaBlurPlaceholders";

// Helper function to generate WebP srcset for 1:1 aspect ratio images
const generateSquareImageSrcset = (imageUrl: string): { webpSrc: string; srcSet: string; sizes: string } => {
  // Extract directory and base filename
  const dir = imageUrl.substring(0, imageUrl.lastIndexOf('/'));
  const filename = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
  const baseName = filename.replace(/\.(jpg|jpeg|png)$/i, '');
  
  // Generate srcset for 1:1 images (desktop, tablet, mobile with 1x and 2x)
  const srcSet = [
    `${dir}/${baseName}.mobile.webp 400w`,
    `${dir}/${baseName}.mobile@2x.webp 800w`,
    `${dir}/${baseName}.tablet.webp 600w`,
    `${dir}/${baseName}.tablet@2x.webp 1200w`,
    `${dir}/${baseName}.desktop.webp 800w`,
    `${dir}/${baseName}.desktop@2x.webp 1600w`
  ].join(', ');
  
  // webpSrc should be the same srcset for the <source> tag
  const webpSrc = srcSet;
  const sizes = '(max-width: 640px) 400px, (max-width: 1024px) 600px, 800px';
  
  return { webpSrc, srcSet, sizes };
};

// Type for tracking quantities per duration
type ServiceQuantities = Record<string, Record<string, number>>;

// --- Static Data ---

import { spaServices as services, saunaFacilities, SHOW_THERAPEUTIC_MASSAGES } from "@/data/spaServices";

// --- Sub-components ---

// Consolidated selector component that handles both single and multiple price scenarios
const ExpandableSelector: React.FC<{
  // For multiple prices (massage carousel)
  serviceId?: string;
  pairs?: Array<{ duration: string; price: string }>;
  getQuantity?: (serviceId: string, duration: string) => number;
  updateQuantity?: (serviceId: string, duration: string, delta: number) => void;
  calculateTotalPrice?: (serviceId: string, pairs: Array<{ duration: string; price: string }>) => number;
  
  // For single price (sauna, pelvipower)
  quantity?: number;
  onIncrease?: () => void;
  onDecrease?: () => void;
  price?: string;
  priceLabel?: string;
  
  // Common props
  isOpen: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  buttonRef: React.RefObject<HTMLButtonElement> | null;
  serviceName: string;
  onBook: () => void;
  position?: 'above' | 'replace' | 'default';
  source?: string; // Source for tracking (massage_carousel, massage_grid, sauna_section, pelvipower_section)
  serviceCategory?: 'massage' | 'sauna' | 'pelvipower'; // Service category for tracking
  serviceIdForTracking?: string; // Service ID for tracking (needed for single price items)
}> = ({ 
  serviceId, 
  pairs, 
  isOpen, 
  onToggle, 
  onClose,
  buttonRef, 
  getQuantity, 
  updateQuantity, 
  calculateTotalPrice, 
  serviceName, 
  onBook,
  // Single price props
  quantity,
  onIncrease,
  onDecrease,
  price,
  priceLabel,
  position = 'default',
  source,
  serviceCategory,
  serviceIdForTracking
}) => {
  const { t } = useLanguage();
  const { setIsTrayOpen } = useServiceList();
  const selectorRef = useRef<HTMLDivElement>(null);
  
  // Determine if this is single price mode
  const isSinglePrice = pairs === undefined && quantity !== undefined;
  
  // Calculate totals
  const singlePriceTotal = isSinglePrice && price ? quantity! * parseInt(price) : 0;
  const multiplePriceTotal = !isSinglePrice && serviceId && pairs && calculateTotalPrice 
    ? calculateTotalPrice(serviceId, pairs) 
    : 0;
  const totalPrice = isSinglePrice ? singlePriceTotal : multiplePriceTotal;
  
  // Check if there are selections
  const hasSinglePriceSelection = isSinglePrice && quantity! > 0;
  const hasMultiplePriceSelections = !isSinglePrice && pairs && getQuantity
    ? pairs.some(pair => getQuantity(serviceId!, pair.duration) > 0)
    : false;
  const hasSelections = isSinglePrice ? hasSinglePriceSelection : hasMultiplePriceSelections;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen || !selectorRef.current) return;
      
      const target = event.target as Node;
      const clickedInsideSelector = selectorRef.current.contains(target);
      const clickedOnButton = buttonRef?.current?.contains(target) || false;
      
      // Close if clicked outside both selector and button
      if (!clickedInsideSelector && !clickedOnButton) {
        if (onClose) {
          onClose();
        } else if (onToggle) {
          onToggle();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle, onClose, buttonRef]);

  if (!isOpen) return null;

  // Determine positioning classes - uniform width across all locations
  // Sauna (replace) needs fixed width wider than the trigger button, responsive for mobile
  const positionClasses = position === 'replace'
    ? 'absolute top-0 left-1/2 -translate-x-1/2 z-30 w-[95%] max-w-[420px] md:min-w-[420px] md:max-w-[500px] md:w-auto'
    : position === 'above'
    ? 'absolute bottom-16 left-1/2 -translate-x-1/2 z-30 w-[90%] max-w-lg'
    : 'absolute bottom-16 left-1/2 -translate-x-1/2 z-30 w-[90%] max-w-lg';

  const handleAddToList = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasSelections && source && serviceCategory) {
      // Calculate item count and selections for tracking
      let itemCount = 0;
      let selections: Array<{ duration: string; quantity: number; price: number }> = [];
      
      if (isSinglePrice && quantity && price) {
        // Single price mode (sauna, pelvipower)
        itemCount = quantity;
        selections = [{
          duration: 'Session',
          quantity: quantity,
          price: parseInt(price)
        }];
      } else if (!isSinglePrice && serviceId && pairs && getQuantity) {
        // Multiple price mode (massage)
        pairs.forEach(pair => {
          const qty = getQuantity(serviceId, pair.duration);
          if (qty > 0) {
            itemCount += qty;
            selections.push({
              duration: pair.duration,
              quantity: qty,
              price: parseInt(pair.price)
            });
          }
        });
      }
      
      // Track "Add to My List" event
      trackSpaSelectorAddToList(
        source,
        serviceIdForTracking || serviceId || 'unknown',
        serviceName,
        serviceCategory,
        itemCount,
        totalPrice,
        selections.length > 0 ? selections : undefined
      );
      
      onBook();
      // Only close if onClose is provided (single price mode)
      // For multiple price mode (onToggle), the parent handles closing
      if (onClose) {
        onClose();
      }
    }
  };

  const handleViewList = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Always open the tray, even if no selections
    // If there are selections, add them first and track the action
    if (hasSelections && source && serviceCategory) {
      // Calculate item count and selections for tracking
      let itemCount = 0;
      let selections: Array<{ duration: string; quantity: number; price: number }> = [];
      
      if (isSinglePrice && quantity && price) {
        // Single price mode (sauna, pelvipower)
        itemCount = quantity;
        selections = [{
          duration: 'Session',
          quantity: quantity,
          price: parseInt(price)
        }];
      } else if (!isSinglePrice && serviceId && pairs && getQuantity) {
        // Multiple price mode (massage)
        pairs.forEach(pair => {
          const qty = getQuantity(serviceId, pair.duration);
          if (qty > 0) {
            itemCount += qty;
            selections.push({
              duration: pair.duration,
              quantity: qty,
              price: parseInt(pair.price)
            });
          }
        });
      }
      
      // Track "Add to My List" event (items are being added)
      trackSpaSelectorAddToList(
        source,
        serviceIdForTracking || serviceId || 'unknown',
        serviceName,
        serviceCategory,
        itemCount,
        totalPrice,
        selections.length > 0 ? selections : undefined
      );
      
      onBook();
    }
    setIsTrayOpen(true, source);
    // Close the popup
    if (onClose) {
      onClose();
    } else if (onToggle) {
      onToggle();
    }
  };

  return (
    <div
      ref={selectorRef}
      className={positionClasses}
      data-oid={serviceId ? `selector-${serviceId}` : 'selector-single'}
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl p-5 w-full">
        <div className="space-y-4 w-full">
          {/* Service Title */}
          <div className="text-center mb-4">
            <h4 className="text-gray-900 font-semibold text-lg mb-1">{serviceName}</h4>
            {isSinglePrice && priceLabel && (
              <p className="text-gray-600 text-sm">{priceLabel}</p>
            )}
          </div>

          {/* Single Price Mode */}
          {isSinglePrice && (
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="text-gray-900 text-sm font-semibold mb-1">Quantity</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDecrease?.();
                  }}
                  disabled={quantity === 0}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-4 h-4 text-gray-600" />
                </button>
                <span className="text-gray-900 text-lg font-semibold min-w-[32px] text-center">{quantity}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onIncrease?.();
                  }}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          )}

          {/* Multiple Price Mode */}
          {!isSinglePrice && pairs && pairs.map((pair, index) => {
            const qty = getQuantity ? getQuantity(serviceId!, pair.duration) : 0;
            return (
              <div key={index} className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="text-gray-900 text-sm font-semibold">{pair.duration} min</div>
                  <div className="text-gray-600 text-xs">CHF {pair.price}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateQuantity?.(serviceId!, pair.duration, -1);
                    }}
                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    disabled={qty === 0}
                  >
                    <Minus className="w-3 h-3 text-gray-600" />
                  </button>
                  <span className="text-gray-900 text-sm font-semibold min-w-[24px] text-center">{qty}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateQuantity?.(serviceId!, pair.duration, 1);
                    }}
                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-3 h-3 text-gray-600" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Always reserve space for total and buttons to prevent layout shift */}
          <div className="pt-3 border-t border-gray-200 min-h-[100px]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-900 text-sm font-semibold">Total:</span>
              <span className={`text-alpine-gold text-xl font-bold transition-opacity duration-200 ${hasSelections ? 'opacity-100' : 'opacity-50'}`}>
                CHF {totalPrice}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAddToList}
                disabled={!hasSelections}
                className="flex-1 bg-alpine-gold hover:bg-alpine-gold/90 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-alpine-gold text-sm px-3 py-2"
              >
                {t("spa.serviceList.addToMyList")}
              </Button>
              <Button
                onClick={handleViewList}
                variant="outline"
                className="flex-1 border-alpine-gold text-alpine-gold hover:bg-alpine-gold hover:text-white text-sm px-3 py-2"
              >
                {t("spa.serviceList.viewMyList")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceCard: React.FC<{
  serviceId: string;
  translationKey: string;
  imageUrl: string;
  priceBoxId: string;
  serviceNameId: string;
  serviceDescId: string;
  serviceCtaId: string;
  isOpen: boolean;
  onToggle: () => void;
  getQuantity: (serviceId: string, duration: string) => number;
  updateQuantity: (serviceId: string, duration: string, delta: number) => void;
  calculateTotalPrice: (serviceId: string, pairs: Array<{ duration: string; price: string }>) => number;
  onBook: () => void;
  source?: string;
}> = ({
  serviceId,
  translationKey,
  imageUrl,
  priceBoxId,
  serviceNameId,
  serviceDescId,
  serviceCtaId,
  isOpen,
  onToggle,
  getQuantity,
  updateQuantity,
  calculateTotalPrice,
  onBook,
  source
}) => {
    const { t, language } = useLanguage();
    const pricing = t(`${translationKey}.pricing`);
    const durationText = t(`${translationKey}.duration`);
    const pairs = extractDurationPricePairs(pricing);
    const durations = extractDurations(durationText);
    const prices = pairs.map(p => p.price);
    const addButtonRef = useRef<HTMLButtonElement>(null);
    
    // Generate srcset for 1:1 aspect ratio images
    const { webpSrc, srcSet, sizes } = generateSquareImageSrcset(imageUrl);

    return (
      <div
        className="group relative h-[350px] md:h-[450px] lg:h-[550px] w-full overflow-hidden cursor-pointer"
        data-oid={serviceId}
      >
        <OptimizedImage
          src={imageUrl}
          alt={getSingleLanguageTitle(t(`${translationKey}.name`), `${translationKey}.name`, language)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
          webpSrc={webpSrc}
          srcSet={srcSet}
          sizes={sizes}
          blurDataURL={getSpaBlurPlaceholder(imageUrl)}
          fetchPriority="auto"
        />

        {/* Sauna-style Dynamic Overlay */}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500" />

        {/* Premium Price Tag - Positioned relative to the new full-width container */}
        <div className="absolute top-8 right-8 bg-black/40 backdrop-blur-md border border-white/30 px-4 py-2 rounded-xl shadow-2xl z-20 transition-transform duration-300 group-hover:scale-105" data-oid={priceBoxId}>
          <div className="text-alpine-gold text-sm font-bold tracking-wider">
            CHF {prices.length > 0 && `${prices[0]}${prices.length > 1 ? ` / ${prices[1]}` : ''}`}
          </div>
          <div className="text-white/90 text-[10px] uppercase tracking-[0.2em] font-lato mt-0.5">
            {durations.length > 0 && `${durations[0]}${durations.length > 1 ? `/${durations[1]}` : ''} min`}
          </div>
        </div>

        <ExpandableSelector
          serviceId={serviceId}
          pairs={pairs}
          isOpen={isOpen}
          onToggle={onToggle}
          buttonRef={addButtonRef}
          getQuantity={getQuantity}
          updateQuantity={updateQuantity}
          calculateTotalPrice={calculateTotalPrice}
          serviceName={getSingleLanguageTitle(t(`${translationKey}.name`), `${translationKey}.name`, language)}
          onBook={() => {
            onBook();
            onToggle();
          }}
          source={source}
          serviceCategory="massage"
          serviceIdForTracking={serviceId}
        />

        {/* Content Area - Sauna-style reveals */}
        <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10 pointer-events-none">
          <div className="transform transition-transform duration-500 group-hover:-translate-y-6">
            <h4 className="text-white font-playfair text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight drop-shadow-lg mb-4" data-oid={serviceNameId}>
              {getSingleLanguageTitle(t(`${translationKey}.name`), `${translationKey}.name`, language)}
            </h4>

            <div className="max-h-0 overflow-hidden opacity-0 group-hover:max-h-48 group-hover:opacity-100 transition-all duration-700 ease-in-out pointer-events-auto">
              <p className="text-white/90 text-sm md:text-base font-lato leading-relaxed mb-8 max-w-sm" data-oid={serviceDescId}>
                {t(`${translationKey}.description`)}
              </p>
            </div>

            <button
              ref={addButtonRef}
              onClick={onToggle}
              className="flex items-center gap-4 text-white hover:text-alpine-gold transition-all duration-300 group/btn pointer-events-auto"
              data-oid={serviceCtaId}
            >
              <span className="font-lato text-xs uppercase tracking-[0.3em] font-semibold border-b border-white/30 pb-1 group-hover/btn:border-alpine-gold">
                {t("spa.treatmentsAndServices.bookNow")}
              </span>
              <div className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm border border-white/20 group-hover/btn:border-alpine-gold flex items-center justify-center transition-all duration-300 transform group-hover/btn:translate-x-2">
                <ArrowRight className="w-4 h-4" />
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  };

// --- Main Component ---

import { toast } from "sonner";
import {
    trackSpaSelectorOpen,
    trackSpaSelectorClose,
    trackSpaSelectorQuantityChange,
    trackSpaSelectorAddToList,
} from "@/utils/analytics";

const TreatmentsAndServicesSection: React.FC = () => {
  const { t, language } = useLanguage();
  const { addItem, setIsTrayOpen } = useServiceList();
  const [isTherapeuticModalOpen, setIsTherapeuticModalOpen] = useState(false);
  const [openSelector, setOpenSelector] = useState<string | null>(null);
  const [openPelviPowerSelector, setOpenPelviPowerSelector] = useState(false);
  const [openSaunaSelector, setOpenSaunaSelector] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSaunaIndex, setCurrentSaunaIndex] = useState(0);

  // Local state for quantity selectors before adding to list
  const [localQuantities, setLocalQuantities] = useState<Record<string, Record<string, number>>>({});
  const [localPelviPowerQty, setLocalPelviPowerQty] = useState(0);
  const [localSaunaQty, setLocalSaunaQty] = useState(0);

  // Track selector open times
  const selectorOpenTimeRef = React.useRef<Record<string, number>>({});

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSaunaIndex((prev) => (prev + 1) % saunaFacilities.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const updateQuantity = (serviceId: string, duration: string, delta: number, source?: string, serviceName?: string) => {
    setLocalQuantities(prev => {
      const serviceQty = prev[serviceId] || {};
      const currentQty = serviceQty[duration] || 0;
      const newQty = Math.max(0, currentQty + delta);
      
      // Track quantity change (after state update, using setTimeout to ensure state is updated)
      if (source && serviceName) {
        setTimeout(() => {
          const service = services.find(s => s.serviceId === serviceId);
          if (service) {
            const pricing = t(`${service.translationKey}.pricing`);
            const pairs = extractDurationPricePairs(pricing);
            const totalPrice = calculateTotalPrice(serviceId, pairs);
            trackSpaSelectorQuantityChange(
              delta > 0 ? 'increment' : 'decrement',
              serviceId,
              serviceName,
              duration,
              currentQty,
              newQty,
              totalPrice
            );
          }
        }, 0);
      }
      
      return {
        ...prev,
        [serviceId]: { ...serviceQty, [duration]: newQty },
      };
    });
  };

  const getQuantity = (serviceId: string, duration: string): number => {
    return localQuantities[serviceId]?.[duration] || 0;
  };

  const calculateTotalPrice = (serviceId: string, pairs: Array<{ duration: string; price: string }>): number => {
    let total = 0;
    pairs.forEach(pair => {
      total += getQuantity(serviceId, pair.duration) * parseInt(pair.price);
    });
    return total;
  };

  const handlePelviPowerBooking = () => {
    if (localPelviPowerQty > 0) {
      const itemId = "pelvipower";
      // Add item multiple times - addItem already handles aggregation
      for (let i = 0; i < localPelviPowerQty; i++) {
        addItem({
          id: itemId,
          serviceId: "pelvipower",
          name: t("spa.treatmentsAndServices.specialPrograms.pelvipower.name"),
          category: "pelvipower",
          price: 29, // Hardcoded for now based on translations
          duration: "60 Min",
          imageUrl: "/images/Clinique Nutraswiss/pelvipower.desktop.webp"
        });
      }
      
      setLocalPelviPowerQty(0);
      setOpenPelviPowerSelector(false);
      toast.success(t("spa.serviceList.addedToList"), {
        duration: 2000,
        position: 'top-center',
      });
    }
  };

  const handleSaunaBooking = () => {
    if (localSaunaQty > 0) {
      const itemId = "sauna-package";
      // Add item multiple times - addItem already handles aggregation
      for (let i = 0; i < localSaunaQty; i++) {
        addItem({
          id: itemId,
          serviceId: "sauna-package",
          name: t("spa.treatmentsAndServices.saunaThermal.package.title"),
          category: "sauna",
          price: 45, // Hardcoded for now
          duration: "Package",
          imageUrl: "/images/Clinique Nutraswiss/Sauna package images/Bio Sauna.jpg"
        });
      }
      
      setLocalSaunaQty(0);
      setOpenSaunaSelector(false);
      toast.success(t("spa.serviceList.addedToList"), {
        duration: 2000,
        position: 'top-center',
      });
    }
  };

  const handleMassageBooking = (serviceId: string, translationKey: string) => {
    const serviceQty = localQuantities[serviceId] || {};
    const pricing = t(`${translationKey}.pricing`);
    const pairs = extractDurationPricePairs(pricing);
    
    // Find the service to get its image
    const service = services.find(s => s.serviceId === serviceId);
    const serviceImageUrl = service?.imageUrl || "/images/massage.jpg";

    pairs.forEach(pair => {
      const qty = serviceQty[pair.duration] || 0;
      if (qty > 0) {
        for (let i = 0; i < qty; i++) {
          addItem({
            id: `${serviceId}-${pair.duration}`,
            serviceId: serviceId,
            name: getSingleLanguageTitle(t(`${translationKey}.name`), `${translationKey}.name`, language),
            category: "massage",
            price: parseInt(pair.price),
            duration: `${pair.duration} Min`,
            imageUrl: serviceImageUrl
          });
        }
      }
    });

    // Clear local selection after adding
    setLocalQuantities(prev => ({ ...prev, [serviceId]: {} }));
    toast.success(t("spa.serviceList.addedToList"), {
      duration: 2000,
      position: 'top-center',
    });
  };

  return (
    <section className="pt-0 pb-20 px-4" data-oid="treatments-services-section">
      <div className="max-w-7xl mx-auto" data-oid="treatments-container">

        {/* Subsection A: Therapeutic Massages — temporarily hidden while massage rooms are under maintenance */}
        {SHOW_THERAPEUTIC_MASSAGES && (
        <div id="therapeutic-massages-section" className="mb-0" data-oid="therapeutic-massages">

          {/* Immersive Wrapper for Massages - Overflow hidden on the parent to allow shadows to bleed from children but stay within section */}
          <div className="relative w-screen left-1/2 -translate-x-1/2 overflow-hidden pt-20 pb-20" data-oid="massages-interaction-wrapper">

            {/* Background Treatment - Transparent for glass effect */}
            {/* <div className="absolute inset-0 bg-gradient-to-b from-gray-100/40 via-gray-50/20 to-elegant-white z-0" /> */}

            {/* Premium Header Overlay - Styled like Sauna Package */}
            <div className="relative w-full z-20 mb-8" data-oid="massages-header-overlay">
              <div className="w-full pt-8 pb-4 px-6 md:px-12">
                <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
                  <GlassContainer intensity="low" className="px-10 py-8 backdrop-blur-md">
                    <h3 className="text-3xl md:text-4xl lg:text-5xl font-playfair font-semibold mb-6 tracking-[0.2em] uppercase text-gray-900 drop-shadow-sm" data-oid="massages-title">
                      {t("spa.treatmentsAndServices.therapeuticMassages.title")}
                    </h3>
                    <div className="w-24 h-px bg-alpine-gold mb-8 mx-auto" />
                    <p className="max-w-4xl text-gray-700 font-lato text-base md:text-lg leading-relaxed mx-auto italic">
                      {t("spa.quickRelaxation.description")}
                    </p>
                  </GlassContainer>
                </div>
              </div>
            </div>

            {/* Carousel/Grid Container */}
            <div className="relative z-10 px-4" data-oid="massages-container">
              {!isExpanded ? (
                <div className="relative w-full" data-oid="massages-carousel">
                  <Carousel
                    setApi={setCarouselApi}
                    opts={{
                      align: "start",
                      loop: false,
                    }}
                    className="w-full"
                  >
                    <div className="flex justify-end gap-4 mb-4 pr-4 md:pr-12 lg:pr-24 relative z-30">
                      <CarouselPrevious className="relative left-0 translate-x-0 border-white/20 hover:bg-white/40 shadow-sm bg-white/20 backdrop-blur-md" />
                      <CarouselNext className="relative right-0 translate-x-0 border-white/20 hover:bg-white/40 shadow-sm bg-white/20 backdrop-blur-md" />
                    </div>

                    <GlassContainer className="w-full overflow-hidden" intensity="medium" data-oid="massages-carousel-frame">
                      <CarouselContent
                        className="-ml-px group/carousel-content"
                        viewportClassName="!overflow-visible"
                      >
                        {services.filter(s => s.category === 'massage' || !s.category).map((service) => (
                          <CarouselItem
                            key={service.serviceId}
                            className="pl-px basis-full md:basis-1/2 lg:basis-1/4 lg:group-hover/carousel-content:basis-[21%] lg:hover:!basis-[37%] transition-all duration-700 ease-in-out"
                          >
                            <ServiceCard
                              {...service}
                              isOpen={openSelector === service.serviceId}
                              onToggle={() => {
                                const wasOpen = openSelector === service.serviceId;
                                if (wasOpen) {
                                  // Closing selector
                                  const serviceName = getSingleLanguageTitle(t(`${service.translationKey}.name`), `${service.translationKey}.name`, language);
                                  const openTime = selectorOpenTimeRef.current[service.serviceId];
                                  const timeSpent = openTime ? Math.floor((Date.now() - openTime) / 1000) : undefined;
                                  const serviceQty = localQuantities[service.serviceId] || {};
                                  const hasSelections = Object.values(serviceQty).some(qty => qty > 0);
                                  const selectionCount = Object.values(serviceQty).filter(qty => qty > 0).length;
                                  
                                  trackSpaSelectorClose('massage_carousel', service.serviceId, serviceName, hasSelections, timeSpent, selectionCount);
                                  delete selectorOpenTimeRef.current[service.serviceId];
                                  setOpenSelector(null);
                                } else {
                                  // Opening selector
                                  const serviceName = getSingleLanguageTitle(t(`${service.translationKey}.name`), `${service.translationKey}.name`, language);
                                  trackSpaSelectorOpen('massage_carousel', service.serviceId, serviceName, 'massage');
                                  selectorOpenTimeRef.current[service.serviceId] = Date.now();
                                  setOpenSelector(service.serviceId);
                                }
                              }}
                              getQuantity={getQuantity}
                              updateQuantity={(serviceId: string, duration: string, delta: number) => updateQuantity(serviceId, duration, delta, 'massage_carousel', getSingleLanguageTitle(t(`${service.translationKey}.name`), `${service.translationKey}.name`, language))}
                              calculateTotalPrice={calculateTotalPrice}
                              onBook={() => handleMassageBooking(service.serviceId, service.translationKey)}
                              source="massage_carousel"
                            />
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                    </GlassContainer>

                    {/* Centered Expand Button below Carousel */}
                    <div className="flex justify-center mt-6">
                      <button
                        onClick={() => {
                          setIsExpanded(true);
                          setTimeout(() => {
                            const element = document.querySelector('[data-oid="therapeutic-massages"]');
                            element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                        className="group flex flex-col items-center gap-3 transition-all duration-300"
                        data-oid="expand-button-carousel"
                      >
                        <div className="w-12 h-12 rounded-full border border-gray-400/50 flex items-center justify-center group-hover:border-alpine-gold transition-colors duration-300 bg-white/20 backdrop-blur-sm">
                          <ChevronDown className="w-6 h-6 text-gray-600 group-hover:text-alpine-gold transition-transform duration-500" />
                        </div>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-gray-600 group-hover:text-alpine-gold transition-colors duration-300">
                          Click to Expand
                        </span>
                      </button>
                    </div>
                  </Carousel>
                </div>
              ) : (
                <>
                  <GlassContainer className="overflow-hidden -mx-4 w-[calc(100%+2rem)]" intensity="medium" data-oid="massages-grid-frame">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px" data-oid="massages-grid">
                      {services.filter(s => s.category === 'massage' || !s.category).map((service) => (
                        <div key={service.serviceId} className="h-[350px] md:h-[450px] lg:h-[550px] overflow-hidden border-r border-b border-white/20 last:border-r-0">
                          <ServiceCard
                            {...service}
                            isOpen={openSelector === service.serviceId}
                            onToggle={() => {
                              const wasOpen = openSelector === service.serviceId;
                              if (wasOpen) {
                                // Closing selector
                                const serviceName = getSingleLanguageTitle(t(`${service.translationKey}.name`), `${service.translationKey}.name`, language);
                                const openTime = selectorOpenTimeRef.current[service.serviceId];
                                const timeSpent = openTime ? Math.floor((Date.now() - openTime) / 1000) : undefined;
                                const serviceQty = localQuantities[service.serviceId] || {};
                                const hasSelections = Object.values(serviceQty).some(qty => qty > 0);
                                const selectionCount = Object.values(serviceQty).filter(qty => qty > 0).length;
                                
                                trackSpaSelectorClose('massage_grid', service.serviceId, serviceName, hasSelections, timeSpent, selectionCount);
                                delete selectorOpenTimeRef.current[service.serviceId];
                                setOpenSelector(null);
                              } else {
                                // Opening selector
                                const serviceName = getSingleLanguageTitle(t(`${service.translationKey}.name`), `${service.translationKey}.name`, language);
                                trackSpaSelectorOpen('massage_grid', service.serviceId, serviceName, 'massage');
                                selectorOpenTimeRef.current[service.serviceId] = Date.now();
                                setOpenSelector(service.serviceId);
                              }
                            }}
                            getQuantity={getQuantity}
                            updateQuantity={(serviceId: string, duration: string, delta: number) => updateQuantity(serviceId, duration, delta, 'massage_grid', getSingleLanguageTitle(t(`${service.translationKey}.name`), `${service.translationKey}.name`, language))}
                            calculateTotalPrice={calculateTotalPrice}
                            onBook={() => handleMassageBooking(service.serviceId, service.translationKey)}
                            source="massage_grid"
                          />
                        </div>
                      ))}
                    </div>
                  </GlassContainer>
                  {/* Grid Expand/Collapse Button (Show Less) */}
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={() => setIsExpanded(false)}
                      className="group flex flex-col items-center gap-3 transition-all duration-300"
                      data-oid="collapse-button-grid"
                    >
                      <div className="w-12 h-12 rounded-full border border-gray-400/50 flex items-center justify-center group-hover:border-alpine-gold transition-colors duration-300 bg-white/20 backdrop-blur-sm">
                        <ChevronDown className="w-6 h-6 text-gray-600 group-hover:text-alpine-gold transition-transform duration-500 rotate-180" />
                      </div>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-gray-600 group-hover:text-alpine-gold transition-colors duration-300">
                        Show Less
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        )}

          {/* Subsection B: Sauna & Thermal Experience */}
          <div className="" data-oid="sauna-thermal">
            <GlassContainer intensity="low" className="relative w-screen left-1/2 -translate-x-1/2 h-[80vh] lg:h-[750px] overflow-hidden group/sauna-section rounded-none border-x-0" data-oid="sauna-interaction-wrapper">
            {/* MOBILE: Background Carousel */}
            <div className="lg:hidden absolute inset-0" data-oid="sauna-mobile-carousel">
              {saunaFacilities.map((facility, index) => {
                const { webpSrc, srcSet, sizes } = generateSquareImageSrcset(facility.image);
                return (
                <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSaunaIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                  <OptimizedImage
                    src={facility.image}
                    alt={getSingleLanguageTitle(t(facility.name), facility.name, language)}
                    className="absolute inset-0 w-full h-full object-cover"
                    webpSrc={webpSrc}
                    srcSet={srcSet}
                    sizes={sizes}
                    blurDataURL={getSpaBlurPlaceholder(facility.image)}
                    fetchPriority="auto"
                  />
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="absolute bottom-12 left-0 w-full text-center px-6 z-20">
                    <p className="text-alpine-gold font-bold text-lg mb-2">{facility.tempKey ? t(facility.tempKey) : facility.tempValue}</p>
                    <h3 className="text-white font-playfair text-3xl mb-4 drop-shadow-lg">{getSingleLanguageTitle(t(facility.name), facility.name, language)}</h3>
                    <p className="text-white/80 text-sm font-lato max-w-xs mx-auto leading-relaxed">{t(facility.description)}</p>
                  </div>
                </div>
                );
              })}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {saunaFacilities.map((_, index) => (
                  <div key={index} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSaunaIndex ? 'bg-alpine-gold w-6' : 'bg-white/30'}`} />
                ))}
              </div>
            </div>

            {/* DESKTOP: 4 Interactive Columns */}
            <div className="hidden lg:flex h-full w-full" data-oid="sauna-grid-desktop">
              {saunaFacilities.map((facility, index) => {
                const { webpSrc, srcSet, sizes } = generateSquareImageSrcset(facility.image);
                return (
                <div key={index} className="relative group/facility h-full overflow-hidden transition-all duration-700 ease-in-out cursor-pointer border-r border-white/10 last:border-r-0 flex-[1] hover:flex-[2]">
                  <OptimizedImage
                    src={facility.image}
                    alt={getSingleLanguageTitle(t(facility.name), facility.name, language)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover/facility:scale-110"
                    webpSrc={webpSrc}
                    srcSet={srcSet}
                    sizes={sizes}
                    blurDataURL={getSpaBlurPlaceholder(facility.image)}
                    fetchPriority="auto"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover/facility:bg-black/20 transition-colors duration-500" />
                  <div className="absolute bottom-12 left-0 w-full text-center px-4 transform transition-transform duration-500 group-hover/facility:-translate-y-4">
                    <p className="text-alpine-gold font-bold text-sm md:text-base mb-1">{facility.tempKey ? t(facility.tempKey) : facility.tempValue}</p>
                    <h3 className="text-white font-playfair text-xl md:text-2xl lg:text-3xl drop-shadow-lg">{getSingleLanguageTitle(t(facility.name), facility.name, language)}</h3>
                    <div className="max-h-0 group-hover/facility:max-h-40 overflow-hidden opacity-0 group-hover/facility:opacity-100 transition-all duration-500 ease-in-out mt-4 px-4">
                      <p className="text-white/90 text-sm font-lato leading-relaxed max-w-xs mx-auto">
                        {t(facility.description)}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Foreground Overlay */}
            <div className="absolute top-0 left-0 w-full z-20 pointer-events-none" data-oid="package-overlay-wrapper">
              <div className="w-full bg-gradient-to-b from-black/90 via-black/60 to-transparent pt-12 pb-16 px-6 md:px-12 pointer-events-auto" data-oid="sauna-package">
                <div className="max-w-7xl mx-auto flex flex-col items-center text-center text-white">
                  <h4 className="text-2xl md:text-3xl lg:text-4xl font-playfair font-semibold mb-3 tracking-[0.2em] uppercase drop-shadow-2xl">
                    {getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language)}
                  </h4>
                  <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-4">
                    <span className="text-xl md:text-2xl font-bold text-alpine-gold drop-shadow-md">{t("spa.treatmentsAndServices.saunaThermal.package.price")}</span>
                    <span className="w-1.5 h-1.5 bg-white/40 rounded-full hidden md:block" />
                    <span className="text-[10px] md:text-xs font-semibold text-white/80 uppercase tracking-[0.3em] font-lato">{t("spa.treatmentsAndServices.saunaThermal.package.hours")}</span>
                  </div>
                  <p className="max-w-4xl text-white/90 font-lato text-sm md:text-base leading-relaxed mx-auto italic drop-shadow-lg">{t("spa.treatmentsAndServices.saunaThermal.package.description")}</p>

                  <div className="relative mt-6">
                    <Button
                      onClick={() => {
                        const wasOpen = openSaunaSelector;
                        if (wasOpen) {
                          // Closing selector
                          const serviceName = getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language);
                          const openTime = selectorOpenTimeRef.current['sauna'];
                          const timeSpent = openTime ? Math.floor((Date.now() - openTime) / 1000) : undefined;
                          trackSpaSelectorClose('sauna_section', 'sauna-package', serviceName, localSaunaQty > 0, timeSpent, localSaunaQty > 0 ? 1 : 0);
                          delete selectorOpenTimeRef.current['sauna'];
                          setOpenSaunaSelector(false);
                        } else {
                          // Opening selector
                          const serviceName = getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language);
                          trackSpaSelectorOpen('sauna_section', 'sauna-package', serviceName, 'sauna');
                          selectorOpenTimeRef.current['sauna'] = Date.now();
                          setOpenSaunaSelector(true);
                        }
                      }}
                      variant="ghost"
                      className="border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-500 rounded-full px-8 py-2 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold backdrop-blur-sm"
                    >
                      {t("spa.treatmentsAndServices.bookNow")}
                    </Button>

                    <ExpandableSelector
                      isOpen={openSaunaSelector}
                      onClose={() => {
                        const serviceName = getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language);
                        const openTime = selectorOpenTimeRef.current['sauna'];
                        const timeSpent = openTime ? Math.floor((Date.now() - openTime) / 1000) : undefined;
                        trackSpaSelectorClose('sauna_section', 'sauna-package', serviceName, localSaunaQty > 0, timeSpent, localSaunaQty > 0 ? 1 : 0);
                        delete selectorOpenTimeRef.current['sauna'];
                        setOpenSaunaSelector(false);
                      }}
                      quantity={localSaunaQty}
                      onIncrease={() => {
                        const previousQty = localSaunaQty;
                        setLocalSaunaQty(prev => prev + 1);
                        const serviceName = getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language);
                        trackSpaSelectorQuantityChange('increment', 'sauna-package', serviceName, 'Session', previousQty, previousQty + 1, (previousQty + 1) * 45);
                      }}
                      onDecrease={() => {
                        const previousQty = localSaunaQty;
                        setLocalSaunaQty(prev => Math.max(0, prev - 1));
                        if (previousQty > 0) {
                          const serviceName = getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language);
                          trackSpaSelectorQuantityChange('decrement', 'sauna-package', serviceName, 'Session', previousQty, previousQty - 1, (previousQty - 1) * 45);
                        }
                      }}
                      price="45"
                      priceLabel={t("spa.treatmentsAndServices.saunaThermal.package.price")}
                      serviceName={getSingleLanguageTitle(t("spa.treatmentsAndServices.saunaThermal.package.title"), "spa.treatmentsAndServices.saunaThermal.package.title", language)}
                      onBook={handleSaunaBooking}
                      buttonRef={null}
                      position="replace"
                      source="sauna_section"
                      serviceCategory="sauna"
                      serviceIdForTracking="sauna-package"
                    />
                  </div>
                </div>
              </div>
            </div>
          </GlassContainer>
        </div>

        {/* Elegant 1px Separator Line */}
        <div className="relative w-screen left-1/2 -translate-x-1/2 h-px bg-white/20 z-30" data-oid="section-separator-line" />

          {/* Subsection C: Specialized Wellness Programs (PelviPower) - Immersive Full Width */}
          <GlassContainer intensity="low" className="relative w-screen left-1/2 -translate-x-1/2 min-h-[85vh] lg:min-h-[800px] overflow-hidden group/special-programs rounded-none border-x-0" data-oid="special-programs-immersive">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <OptimizedImage
              src="/images/Clinique Nutraswiss/pelvipower.desktop.webp"
              alt="PelviPower Pelvic Floor Training"
              className="w-full h-full object-cover transition-transform duration-1000 group-hover/special-programs:scale-110"
              webpSrc="/images/Clinique Nutraswiss/pelvipower.mobile.webp 768w, /images/Clinique Nutraswiss/pelvipower.tablet.webp 1024w, /images/Clinique Nutraswiss/pelvipower.desktop.webp 1920w"
              srcSet="/images/Clinique Nutraswiss/pelvipower.mobile.webp 768w, /images/Clinique Nutraswiss/pelvipower.tablet.webp 1024w, /images/Clinique Nutraswiss/pelvipower.desktop.webp 1920w"
              sizes="100vw"
              blurDataURL={getSpaBlurPlaceholder("/images/Clinique Nutraswiss/pelvipower.desktop.webp")}
              fetchPriority="auto"
              data-oid="pelvipower-bg-immersive"
            />
            {/* Multi-layered Gradient for Text Readability - Black Theme like Sauna Section */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent lg:block hidden" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent lg:hidden block" />
            <div className="absolute inset-0 bg-black/60 lg:hidden block" />
          </div>

          {/* Content Container - Aligned with Sauna Section Style */}
          <div className="relative z-10 w-full h-full flex items-center pt-32 pb-24 px-6 md:px-12 lg:px-24">
            <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-16 items-center">

              {/* Left Side: Information & Benefits */}
              <div className="text-white space-y-8" data-oid="pelvipower-info-col">
                <div className="space-y-4">
                  <h3 className="text-3xl md:text-5xl lg:text-6xl font-playfair font-semibold leading-tight drop-shadow-xl" data-oid="pelvipower-title-immersive">
                    {t("spa.treatmentsAndServices.specialPrograms.pelvipower.type")}
                  </h3>
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-0.5 bg-alpine-gold" />
                    <p className="text-alpine-gold font-bold tracking-[0.2em] uppercase text-xs md:text-sm">
                      {t("spa.treatmentsAndServices.specialPrograms.pelvipower.name")}
                    </p>
                  </div>
                </div>

                <p className="text-lg md:text-xl text-white/90 font-lato leading-relaxed max-w-4xl italic">
                  "{t("spa.treatmentsAndServices.specialPrograms.pelvipower.description")}"
                </p>

                {/* Glassmorphic Concept Steps - Unified with Section Aesthetic */}
                <div className="flex flex-wrap items-center gap-4 md:gap-6 pt-4" data-oid="pelvipower-steps-process">
                  {[
                    { num: "01", label: "Vitalize", icon: <Zap className="w-5 h-5 md:w-6 md:h-6" /> },
                    { num: "02", label: "Train", icon: <Dumbbell className="w-5 h-5 md:w-6 md:h-6" /> },
                    { num: "03", label: "Maintain", icon: <TrendingUp className="w-5 h-5 md:w-6 md:h-6" /> }
                  ].map((step) => (
                    <div
                      key={step.num}
                      className="flex-1 min-w-[110px] bg-white/10 backdrop-blur-md border border-white/20 p-4 md:p-5 rounded-2xl transition-all duration-500 hover:bg-white/20 group/step cursor-pointer shadow-xl relative overflow-hidden"
                    >
                      {/* Subdued Number Accent */}
                      <span className="absolute -top-1 -right-1 text-3xl font-bold text-white/[0.05] group-hover/step:text-white/[0.1] transition-all duration-500">
                        {step.num}
                      </span>

                      <div className="flex flex-col items-center text-center space-y-3 relative z-10">
                        <div className="w-10 h-10 rounded-full bg-alpine-gold/20 flex items-center justify-center text-alpine-gold group-hover/step:bg-alpine-gold group-hover/step:text-black transition-all duration-500">
                          {step.icon}
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-xs md:text-sm font-bold text-white uppercase tracking-wider">{step.label}</h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Benefits Grid - Enhanced Prominence */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 pt-8 border-t border-white/10" data-oid="pelvipower-features-grid">
                  {[
                    { key: "women", title: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.women.title"), desc: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.women.desc") },
                    { key: "men", title: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.men.title"), desc: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.men.desc") },
                    { key: "incontinence", title: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.incontinence.title"), desc: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.incontinence.desc") },
                    { key: "back", title: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.back.title"), desc: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.back.desc") },
                    { key: "mothers", title: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.mothers.title"), desc: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.mothers.desc") },
                    { key: "sports", title: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.sports.title"), desc: t("spa.treatmentsAndServices.specialPrograms.pelvipower.benefits.sports.desc") }
                  ].map((benefit) => (
                    <div key={benefit.key} className="bg-white/[0.03] backdrop-blur-sm border border-white/5 p-4 rounded-xl space-y-2 transition-all duration-300 hover:bg-white/[0.07] hover:border-white/10 group/benefit">
                      <p className="font-bold text-alpine-gold text-[10px] md:text-xs uppercase tracking-[0.15em] transition-colors">{benefit.title}</p>
                      <p className="text-white/90 text-xs md:text-sm leading-snug">{benefit.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-6 pt-6">
                  {/* Premium Price Tag - Carousel Style */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-2xl shadow-2xl transition-transform duration-300 hover:scale-105 w-fit" data-oid="pelvipower-price-premium">
                    <div className="text-alpine-gold text-lg md:text-xl font-bold tracking-wider">
                      {t("spa.treatmentsAndServices.specialPrograms.pelvipower.price").split(' (')[0]}
                    </div>
                    <div className="text-white/60 text-[10px] uppercase tracking-[0.3em] font-lato mt-0.5">
                      {t("spa.treatmentsAndServices.specialPrograms.pelvipower.price").split(' (')[1]?.replace(')', '') || "Treatment"}
                    </div>
                  </div>

                  {/* Carousel Style Book Button */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        const wasOpen = openPelviPowerSelector;
                        if (wasOpen) {
                          // Closing selector
                          const serviceName = t("spa.treatmentsAndServices.specialPrograms.pelvipower.name");
                          const openTime = selectorOpenTimeRef.current['pelvipower'];
                          const timeSpent = openTime ? Math.floor((Date.now() - openTime) / 1000) : undefined;
                          trackSpaSelectorClose('pelvipower_section', 'pelvipower', serviceName, localPelviPowerQty > 0, timeSpent, localPelviPowerQty > 0 ? 1 : 0);
                          delete selectorOpenTimeRef.current['pelvipower'];
                          setOpenPelviPowerSelector(false);
                        } else {
                          // Opening selector
                          const serviceName = t("spa.treatmentsAndServices.specialPrograms.pelvipower.name");
                          trackSpaSelectorOpen('pelvipower_section', 'pelvipower', serviceName, 'pelvipower');
                          selectorOpenTimeRef.current['pelvipower'] = Date.now();
                          setOpenPelviPowerSelector(true);
                        }
                      }}
                      className="flex items-center gap-4 text-white hover:text-alpine-gold transition-all duration-300 group/btn w-fit"
                      data-oid="pelvipower-book-btn-premium"
                    >
                      <span className="font-lato text-xs md:text-sm uppercase tracking-[0.3em] font-bold border-b border-white/30 pb-1 group-hover/btn:border-alpine-gold transition-all">
                        {t("spa.treatmentsAndServices.bookNow")}
                      </span>
                      <div className="w-12 h-12 rounded-full border border-white/30 group-hover/btn:border-alpine-gold flex items-center justify-center transition-all duration-300 transform group-hover/btn:translate-x-2 bg-white/5 backdrop-blur-sm">
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </button>

                    <ExpandableSelector
                      isOpen={openPelviPowerSelector}
                      onClose={() => {
                        const serviceName = t("spa.treatmentsAndServices.specialPrograms.pelvipower.name");
                        const openTime = selectorOpenTimeRef.current['pelvipower'];
                        const timeSpent = openTime ? Math.floor((Date.now() - openTime) / 1000) : undefined;
                        trackSpaSelectorClose('pelvipower_section', 'pelvipower', serviceName, localPelviPowerQty > 0, timeSpent, localPelviPowerQty > 0 ? 1 : 0);
                        delete selectorOpenTimeRef.current['pelvipower'];
                        setOpenPelviPowerSelector(false);
                      }}
                      quantity={localPelviPowerQty}
                      onIncrease={() => {
                        const previousQty = localPelviPowerQty;
                        setLocalPelviPowerQty(prev => prev + 1);
                        const serviceName = t("spa.treatmentsAndServices.specialPrograms.pelvipower.name");
                        trackSpaSelectorQuantityChange('increment', 'pelvipower', serviceName, 'Session', previousQty, previousQty + 1, (previousQty + 1) * 29);
                      }}
                      onDecrease={() => {
                        const previousQty = localPelviPowerQty;
                        setLocalPelviPowerQty(prev => Math.max(0, prev - 1));
                        if (previousQty > 0) {
                          const serviceName = t("spa.treatmentsAndServices.specialPrograms.pelvipower.name");
                          trackSpaSelectorQuantityChange('decrement', 'pelvipower', serviceName, 'Session', previousQty, previousQty - 1, (previousQty - 1) * 29);
                        }
                      }}
                      price="29"
                      priceLabel={t("spa.treatmentsAndServices.specialPrograms.pelvipower.price")}
                      serviceName={t("spa.treatmentsAndServices.specialPrograms.pelvipower.name")}
                      onBook={handlePelviPowerBooking}
                      buttonRef={null}
                      position="above"
                      source="pelvipower_section"
                      serviceCategory="pelvipower"
                      serviceIdForTracking="pelvipower"
                    />
                  </div>
                </div>
              </div>

              {/* Right Side Spacer */}
              <div className="hidden lg:block h-full w-full" data-oid="pelvipower-right-spacer" />

            </div>
          </div>
          </GlassContainer>
        </div>

      <TherapeuticTreatmentsModal isOpen={isTherapeuticModalOpen} onClose={() => setIsTherapeuticModalOpen(false)} />
    </section>
  );
};

export default TreatmentsAndServicesSection;
